import React from 'react';

import { MultiStepModal } from '../../multiStepModal';
import { ModalStepTree } from '../../multiStepModal/types';
import { AccountTypePickerModalStep } from './steps/accountTypePickerModalStep';
import { AccountTypeFormModalStep } from './steps/accountTypeFormModalStep';
import { AccountPickerModalStep } from './steps/accountPickerModalStep';
import { DeleteAccountConfirmationModalStep } from './steps/deleteAccountConfirmationModalStep';
import { AccountEditFormModalStep } from './steps/accountEditFormModalStep';
import { useDesignVariant } from '../designVariantContext';
import { useAccountManagementStore } from '../store';

interface SelectAccountModalProps {
  open: boolean;
  onClose: () => void;
  selectedAccountId?: string;
  initialAccountId?: string;
}

export const SelectAccountModal: React.FC<SelectAccountModalProps> = ({
  open,
  onClose,
  selectedAccountId,
  initialAccountId,
}) => {
  const variant = useDesignVariant();
  const { adapter } = useAccountManagementStore();

  // The create branch of the select modal ("Add account"): a resource may swap in its
  // own create tree. That tree must keep the shared `accountTypePicker` root key so
  // the picker's navigation stays the same for every resource type.
  const createBranch: ModalStepTree = adapter?.getCreateStepTree?.() ?? {
    key: 'accountTypePicker',
    title: 'Create account to connect',
    component: () => <AccountTypePickerModalStep />,
    children: [
      {
        key: 'accountTypeForm',
        title: 'Configure account',
        component: () => <AccountTypeFormModalStep />,
      },
    ],
  };

  return (
    <MultiStepModal
      showCross
      variant={variant}
      steps={{
        key: 'accountPicker',
        title: 'Select a cloud account to connect',
        component: () => (
          <AccountPickerModalStep
            selectedAccountId={selectedAccountId}
            initialAccountId={initialAccountId}
          />
        ),
        children: [
          createBranch,
          {
            key: 'deleteAccountConfirmation',
            title: 'Delete account?',
            component: () => <DeleteAccountConfirmationModalStep />,
          },
          {
            key: 'editAccountForm',
            title: 'Edit account',
            component: () => <AccountEditFormModalStep />,
          },
        ],
      }}
      open={open}
      onClose={onClose}
      breadcrumb={{ hide: true }}
      size="lg"
    />
  );
};
